/**
 * Discard-on-leave for abandoned product stubs (Jake, 2026-08-27).
 *
 * NEVER A PRODUCT THAT HAD A SKU (card #542). The no-SKU rule below is for a
 * draft nobody finished. `hasEverHadSku` (sticky, from the store) marks a
 * product that carried a SKU when loaded or at any point in the session; if it
 * is blank now (a supplier switch, a failed lookup), the guard keeps it and
 * never offers deletion.
 *
 * Builds the async route-leave guard ProductEditor.vue installs via
 * onBeforeRouteLeave. Split out of the component so the decision and its
 * effects are testable as behaviour -- what the router is told, and what the
 * server is asked to delete -- without mounting the whole editor.
 *
 * WHICH NAVIGATIONS ARE GUARDED
 * -----------------------------
 * GUARDED: every in-app router navigation that leaves this product -- the back
 * arrow (a router.push to store_edit), the app-shell sidebar, opening a
 * different product, and the browser BACK/FORWARD buttons (vue-router runs
 * beforeRouteLeave on popstate too, and reverts the history entry when the
 * guard returns false).
 *
 * NOT GUARDED: tab closes, full page reloads, and typing a new URL in the
 * address bar. Those only surface through `beforeunload`, which cannot show
 * custom text, cannot be cancelled programmatically, and cannot reliably
 * complete an authenticated DELETE. Deliberately NOT wired: a beforeunload
 * handler here would produce a browser-generic "Leave site?" prompt on a
 * perfectly ordinary tab close, which is worse than the leaked row it would
 * only sometimes prevent. A stub abandoned by closing the tab therefore
 * survives -- the user can delete it from the Products page as they can today.
 *
 * ALSO NOT GUARDED: the store editor's own section nav, because it is not
 * reachable from inside the product editor -- the product editor is a
 * top-level route, not a section of the store editor shell.
 *
 * HOW THE DELETE AVOIDS THE UNLOAD RACE
 * -------------------------------------
 * The DELETE is issued and AWAITED inside the guard, BEFORE the navigation is
 * allowed to proceed. It therefore never runs during component teardown, where
 * an in-flight request can be cancelled by the unmount/unload and the row
 * leaks anyway. The cost is a short wait on a confirmed discard, which is the
 * correct trade: the whole point of the feature is that the row is gone.
 *
 * A FAILED delete still allows the navigation. Trapping a user in an editor
 * because a cleanup request failed would be a worse bug than the stray row;
 * the failure is reported through the app's normal error channel by the
 * caller-supplied deleteStub.
 *
 * A PRODUCT WITH NO SKU ALWAYS ASKS (card #233, 2026-10-21)
 * ---------------------------------------------------------
 * Before the rule below is consulted: if the product's effective SKU is
 * blank (and it has never been ordered), leaving always shows the dialog,
 * whatever the user did. That is Jake's 2026-08-27 rule as he stated it; the
 * narrower rule below had kept every touched SKU-less product silently.
 * The dialog says whether the user's work would be lost, and Stay keeps them
 * on the page to pick a SKU. The rule below now governs only products that
 * HAVE a SKU (the auto-filled-SKU orphans it was written for).
 *
 * WHAT DECIDES "ABANDONED" (revised 2026-09-12, Jake)
 * ---------------------------------------------------
 * TWO conditions, and BOTH must hold:
 *
 *   1. the productEditor store's STICKY authoring-intent flag is CLEAN, and
 *   2. `hasAuthoredContent` finds nothing a person could have typed.
 *
 * Condition 1 is new and is the primary gate. The original design inferred
 * "untouched" from field presence alone, and that broke in production: on
 * sandbox 8083 all 19 orphaned rows in store 44 carried real, distinct SanMar
 * SKUs and resolved suppliers despite being named "New product" and never
 * configured. Something other than the user writes that SKU, so a blank-SKU
 * test could not see the abandonment it existed to detect and the dialog never
 * appeared -- reproduced three ways (browser back, back-arrow from the product
 * editor, back-arrow from the store editor).
 *
 * Direct intent tracking is immune to that, because it observes the user
 * rather than the row: an auto-filled SKU no longer blocks a discard, and no
 * future auto-fill path can silently re-break the feature.
 *
 * Condition 2 is `hasAuthoredContent`, NOT the older `isAbandonedProductStub`.
 * The difference is precisely the SKU and supplier columns: that predicate
 * requires them blank, and requiring that here would reinstate the bug, since
 * the abandoned rows all carry them. What remains -- a renamed product, or any
 * populated user-authorable field -- is content only a person can produce, so
 * it is still a real second opinion and not a rubber stamp. Every disagreement
 * between the two conditions resolves to "keep the row", preserving the safe
 * direction below.
 *
 * SCOPE: the flag is in-memory and does NOT survive a page reload. A user who
 * creates a product, configures it, reloads, and then leaves keeps the row
 * rather than being offered the discard. Jake accepted this explicitly --
 * "this is an 80/20 case, and reload is firmly in the 20% camp" -- and it
 * errs toward keeping, which is the direction that cannot destroy work. Do not
 * add sessionStorage or a server-side touched flag to close it.
 */
import { hasAuthoredContent, hasNoSku } from "./abandonedProductStub";

// Route names that ARE the product editor. Both mount ProductEditor and both
// carry :campaignProductId, so a move between them for the SAME product is
// internal (a tab click or a placement selection), not a departure.
const EDITOR_ROUTE_NAMES = new Set([
    "product_editor",
    "product_editor_placement",
]);

/**
 * Does navigating to `to` leave THIS product's editor?
 *
 * @param {object} to  vue-router target location.
 * @param {string|number|null} currentCampaignProductId
 * @returns {boolean}
 */
export function isLeavingEditor(to, currentCampaignProductId) {
    if (!to || !EDITOR_ROUTE_NAMES.has(to.name)) return true;
    const targetId = to.params?.campaignProductId;
    if (targetId == null || currentCampaignProductId == null) return true;
    // Route params arrive as strings; the store may hold a number. Compare as
    // strings so a numeric id never reads as a different product.
    return String(targetId) !== String(currentCampaignProductId);
}

/**
 * Build the route-leave guard.
 *
 * @param {object} deps
 * @param {() => string|number|null} deps.campaignProductId  Current product id.
 * @param {() => object|null} deps.attributes  EFFECTIVE attribute bag
 *   (dirty-over-server) for the current product.
 * @param {() => boolean} deps.hasAuthoringIntent  Reads the productEditor
 *   store's STICKY authoring-intent flag. True iff the user took any
 *   authoring action on this product since the editor opened it. A missing
 *   dep reads as "intent unknown" and suppresses the discard.
 * @param {(id: string|number) => Promise<void>} deps.deleteStub  Issues the
 *   server delete. Owns its own error reporting.
 * @param {() => Promise<boolean>} deps.requestConfirmation  Shows the warning
 *   and resolves true to discard-and-leave, false to stay.
 * @returns {(to: object) => Promise<boolean|undefined>} guard -- resolves
 *   `false` to CANCEL the navigation, otherwise `undefined` to allow it.
 */
export function createStubDiscardGuard({
    campaignProductId,
    attributes,
    hasAuthoringIntent,
    hasEverHadSku,
    deleteStub,
    requestConfirmation,
}) {
    return async function guardRouteLeave(to) {
        const id = campaignProductId ? campaignProductId() : null;
        // No id means the editor never resolved a product. Nothing to discard,
        // and nothing we could safely delete.
        if (id == null || id === "") return undefined;

        // Internal tab / placement navigation: silent, always.
        if (!isLeavingEditor(to, id)) return undefined;

        // NO SKU: always ask (card #233; Jake 2026-08-27, "warn when
        // navigating out that the product will be removed/deleted with the
        // option to cancel navigation"). The gates below only decide whether
        // a product that HAS a SKU was really configured; a product without
        // one cannot be sold whatever else was done to it, and keeping it
        // silently is the orphaned "New product" shell. Nothing is deleted
        // without the user's confirmation, and the confirmation is told
        // whether the user did any work so it never says "nothing will be
        // lost" when something will.
        const attrs = attributes ? attributes() : null;
        if (hasNoSku(attrs)) {
            // A product that HAD a SKU (loaded with one, or picked one in this
            // session) is a configured product that lost it -- a supplier
            // switch, a failed lookup -- NOT a draft nobody finished. It is
            // never offered for deletion (card #542: Discard on exactly this
            // state deleted a saved product). Keep it and let the user leave;
            // the missing SKU shows up in the readiness banner instead.
            if (typeof hasEverHadSku === "function" && hasEverHadSku() === true) {
                return undefined;
            }
            const authored = (typeof hasAuthoringIntent === "function"
                && hasAuthoringIntent() === true)
                || hasAuthoredContent(attrs);
            return confirmThenDiscard(id, { authored, productName: attrs?.product_name });
        }

        // PRIMARY GATE: did the user ever author anything here?
        //
        // This is the condition that makes the feature work at all. Field
        // presence alone could not tell "the system wrote a SKU" from "a
        // person picked a SKU", and on sandbox 8083 every abandoned row
        // carried a real SanMar SKU written by something other than the user
        // -- so the field check returned false and no dialog ever appeared.
        //
        // A MISSING dep reads as intent, not as absence of it: an unwired
        // caller must fail toward keeping the row, never toward deleting it.
        if (typeof hasAuthoringIntent !== "function") return undefined;
        if (hasAuthoringIntent()) return undefined;

        // SECOND OPINION: does the payload carry USER-AUTHORED content?
        //
        // Deliberately NOT `isAbandonedProductStub`. That predicate also
        // requires a blank SKU and no supplier linkage, and those three fields
        // are exactly the ones written by the non-user path -- gating on them
        // here would reinstate the bug the intent flag exists to fix, because
        // the 8083 rows all carry them. `hasAuthoredContent` drops those three
        // and keeps the checks nothing but a person can trip: a renamed
        // product, or any populated user-authorable field.
        //
        // Both conditions must agree before anything is deleted, so a
        // disagreement always resolves to keeping the row (module header's
        // safe direction). It also means a page reload, which loses the
        // in-memory flag, still cannot delete visibly-authored work.
        if (hasAuthoredContent(attrs)) return undefined;

        return confirmThenDiscard(id, { authored: false, productName: attrs?.product_name });
    };

    async function confirmThenDiscard(id, { authored, productName }) {
        const confirmed = await requestConfirmation({ authored, productName });
        if (!confirmed) {
            // Explicit false is the ONLY value that keeps vue-router on the
            // current route. Returning undefined here would dismiss the dialog
            // and navigate anyway -- the exact bug a "did a dialog appear?"
            // assertion would miss.
            return false;
        }

        // Await the delete so it cannot be cancelled by teardown. A failure is
        // reported by deleteStub; we still let the user leave.
        try {
            await deleteStub(id);
        } catch {
            // Intentionally swallowed -- see module header.
        }
        return undefined;
    }
}
